import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { camelToSnake, kpiValue, toCamelCase } from '../../../../utils/farecast-util';
import { AppContext } from '../../../../contexts/AppContext';
import APIList from '../../../../http/ApiList';
import { COMPARE, FIELD_CATEGORY, FIELD_IMPORTANT, PAGE_ID, SORT_SHOW } from '../../../../enum/common.enum';
import { differenceWith, groupBy, isEqual, isNil, map, max, min, orderBy, sortBy, sumBy, union, uniq, uniqBy } from 'lodash';
import { getS3Object } from '@meteor/frontend-core';
import { Breadcrumb, Card, Flex, Select, Space, Switch, Tag } from 'antd';
import { GLOBAL_EVENT } from '../../../../consts/common/global-event';
import ContentChart from '../../../base/content-chart';
import { CloseCircleOutlined, CloseOutlined } from '@ant-design/icons';
import { NAME_PATH, useTralitSetting } from '../../../../hooks/useTralitSetting';
import { useNavigate } from 'react-router-dom';
import { useLabel } from '../../../../hooks/useLabel';
import dayjs from 'dayjs';

const CustomizeCompareChart: React.FC<any> = (props) => {
  const ALL_VALUE = 'ALL';
  const ALL_LABEL = '差分';
  const { property } = props;
  const { extra = true, condition, eventType, action = 'drillDown', showPlan = false, sort } = property;
  const { color, event$, tralitSettings } = useContext(AppContext);
  const { fontSize } = useTralitSetting({ namePath: NAME_PATH.PREFERENCE });
  const label = useLabel();
  const navigate = useNavigate();
  // 完整数据
  const [dataProvider, setDataProvider] = useState({
    fields: [],
    targetData: [],
    currentData: [],
    currentRiskPotentialData: [],
    targetRiskPotentialData: [],
  });
  const [planData, setPlanData] = useState([]);
  // Loading
  const [chartLoading, setChartLoading] = useState<any>(true);
  // 比较KPI Fields Key
  const [compareKpiKey, setCompareKpiKey] = useState();
  // 比较KPI Fields
  const [fieldKpi, setFieldKpi] = useState<any>({});
  // useMemo(() => {
  //   const { fields } = dataProvider;
  //   if (compareKpiKey) {
  //     return fields.find((field) => compareKpiKey === field.fieldKey) || {};
  //   }
  //   const findCompareKPI = fields.find((field) => field.category === FIELD_CATEGORY.KPI_DATA && field.isCompare);
  //   return findCompareKPI || {};
  // }, [dataProvider, compareKpiKey]);
  // // Drill Down Fields
  const [drillFields, setDrillFields] = useState([]);
  // 比较Drill Down Field
  const [compareFieldKey, setCompareFieldKey] = useState<any>('');
  // Target Label
  const [tarLabel, setTarLabel] = useState();
  // Current Label
  const [curLabel, setCurLabel] = useState();
  //
  const [title, setTitle] = useState();
  //
  const [totalShow, setTotalShow] = useState(true);
  //
  const [sortShow, setSortShow] = useState(sort || SORT_SHOW.DEVIATION);
  //
  const [mergeAll, setMergeAll] = useState(false);

  const OTHER_LABEL = 'その他';

  const [chartFilter, setChartFilter] = useState([]);

  const [compare, setCompare] = useState();

  const [showCondition, setShowCondition] = useState([]);

  // // URL Params
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  useEffect(() => {
    const params = Object.fromEntries(searchParams);
    if (params.actionCompare === COMPARE.SNAPSHOT_RATIO) {
      setSortShow(SORT_SHOW.TREND);
    }
  }, [searchParams]);

  useEffect(() => {
    if (eventType) {
      setTimeout(() => {
        event$.emit({
          pageId: PAGE_ID.COMPARE,
          i: property.i,
          eventType,
          data: condition,
        });
      }, 50);
    }
  }, [eventType, condition, tralitSettings.account]);

  /**
   * 调用API获取数据
   */
  const getChartData = (req) => {
    const {
      fiscalYear,
      compare,
      aggregationPeriod,
      drillSaveFields = [],
      metric,
      currentSnapshot,
      targetSnapshot,
      currentPeriod = [],
      targetPeriod = [],
    } = req;

    const [startDate, endDate] = aggregationPeriod || [];

    setChartLoading(true);
    const paramMap = {
      snapshot: {
        snapshot: currentSnapshot && targetSnapshot ? [currentSnapshot, targetSnapshot] : undefined,
        compare: 'snapshot',
        reportType: 'monthly',
      },
      period: {
        snapshot: currentSnapshot ? [currentSnapshot] : undefined,
        compare: 'period',
        reportType: 'monthly',
        timeFilter:
          currentPeriod.length > 1
            ? [
                {
                  startDate: currentPeriod[0]?.startOf('month').format('YYYY-MM-DD'),
                  endDate: currentPeriod[1]?.endOf('month').format('YYYY-MM-DD'),
                },
                {
                  startDate: targetPeriod[0]?.startOf('month').format('YYYY-MM-DD'),
                  endDate: targetPeriod[1]?.endOf('month').format('YYYY-MM-DD'),
                },
              ]
            : undefined,
      },
      plan: {
        snapshot: currentSnapshot ? [currentSnapshot] : undefined,
        compare: 'plan',
        reportType: 'monthly',
      },
      prev_year: {
        snapshot: currentSnapshot ? [currentSnapshot] : undefined,
        compare: 'prev_year',
        reportType: 'monthly',
      },
      prev_month: {
        snapshot: currentSnapshot ? [currentSnapshot] : undefined,
        compare: 'prev_month',
        reportType: 'monthly',
      },
    };
    const params = {
      websocketRoute: 'WebsocketOpenaiEvent',
      client_account: tralitSettings.account,
      fiscalYear,
      compare,
      startDate: startDate?.format('YYYY-MM-DD'),
      endDate: endDate?.format('YYYY-MM-DD'),
      // drillFields: req.drillFields,
      ...paramMap[compare],
    };
    setChartFilter(
      Object.keys(req.drillFields || {}).reduce((org, fieldKey) => {
        const value = req.drillFields[fieldKey];
        if (value && value.length > 0) {
          org.push({
            fieldKey,
            value,
          });
        }
        return org;
      }, [])
    );
    Promise.all([
      APIList.getCompareChartData().post(params),
      APIList.getCompareChartData().post({ ...params, compare: COMPARE.PLANNED_RATIO }),
    ])
      .then(async ([data, plan]) => {
        const dataResponse = await getS3Object(process.env.REACT_APP_BUCKET_NAME, data.bucketKey);
        const planResponse = await getS3Object(process.env.REACT_APP_BUCKET_NAME, plan.bucketKey);
        setPlanData(planResponse.targetData);

        const fields = dataResponse.fields;
        const condition = dataResponse.condition;
        const tarLabelMap = {
          snapshot: `${dayjs(targetSnapshot || condition.snapshot[1]).format('MM-DD')}`,
          period: `期間合計${
            targetPeriod.length > 0 ? `(${targetPeriod[0].format('YYYY-MM')} ~ ${targetPeriod[1].format('YYYY-MM')})` : ''
          }`,
          plan: label.plan,
          prev_year: '前年',
          prev_month: '前月',
        };
        const curLabelMap = {
          snapshot: `${dayjs(currentSnapshot || condition.snapshot[0]).format('MM-DD')}`,
          period: `期間合計${
            currentPeriod.length > 0 ? `(${currentPeriod[0].format('YYYY-MM')} ~ ${currentPeriod[1].format('YYYY-MM')})` : ''
          }`,
          plan: '合計',
          prev_year: '今年',
          prev_month: '今月',
        };

        setTarLabel(tarLabelMap[condition.compare]);
        setCurLabel(curLabelMap[condition.compare]);
        setCompare(condition.compare);
        const compareKpiKey = metric || fields.find((field) => field.category === FIELD_CATEGORY.KPI_DATA && field.isCompare)?.fieldKey;
        const fieldKpi = fields.find((field) => compareKpiKey === field.fieldKey) || {};
        const titleMap = {
          snapshot: `断面比較(${fieldKpi.label || ''})`,
          period: `期間比較(${fieldKpi.label || ''})`,
          plan: `${label.plan}比較(${fieldKpi.label || ''})`,
          prev_year: `前年比較(${fieldKpi.label || ''})`,
          prev_month: `前月比較(${fieldKpi.label || ''})`,
        };
        setTitle(titleMap[condition.compare]);
        setCompareKpiKey(compareKpiKey);
        setFieldKpi(fieldKpi);
        setCompareFieldKey(
          drillSaveFields && drillSaveFields.length > 0
            ? drillSaveFields.map((drillSaveField) => drillSaveField.fieldKey).join('&')
            : fields
                .filter((field) => field.category === FIELD_CATEGORY.DRILL_DOWN && field.isCompare)
                .map((field) => field.fieldKey)
                .join('&')
        );
        setDataProvider(dataResponse);
        setDrillFields(
          sortBy(
            dataResponse.fields.filter(
              (field) => field.category === FIELD_CATEGORY.DRILL_DOWN && field.classification?.includes('fluctuation')
            ),
            (item) => (!isNil(item.level) ? item.level : item.priority)
          )
        );
      })
      .finally(() => {
        setChartLoading(false);
      });
  };

  /**
   * 事件监听：PAGE_INIT， FILTER_ITEM_CHANGED
   */
  event$.useSubscription((globalEvent) => {
    if (!globalEvent || globalEvent.pageId !== PAGE_ID.COMPARE) {
      return;
    }
    if (globalEvent.i && property.i !== globalEvent.i) {
      return;
    }
    if (globalEvent.eventType === GLOBAL_EVENT.PAGE_INIT) {
      getChartData({
        ...globalEvent.data,
        ...(condition || {}),
      });
    }
    if (globalEvent.eventType === GLOBAL_EVENT.FILTER_ITEM_CHANGED) {
      getChartData(globalEvent.data);
    }
    if (globalEvent.eventType === GLOBAL_EVENT.CHART_ITEM_CLICK) {
      const { fieldKey, value } = globalEvent.data;
      const index = drillFields.findIndex((field) => field.fieldKey === fieldKey);
      const before = drillFields[index];
      const next = drillFields[index + 1];
      if (value && next && !fieldKey.includes('&')) {
        setCompareFieldKey(next.fieldKey);
        setChartFilter((chartFilter) => [...chartFilter, { fieldKey, value }]);
      }
      if (!value && before && !fieldKey.includes('&')) {
        setCompareFieldKey(before.fieldKey);
        setChartFilter((chartFilter) => chartFilter.filter((item) => item.fieldKey !== fieldKey));
      }
    }
    if (globalEvent.eventType === GLOBAL_EVENT.CONDITION_CHANGE) {
      setShowCondition(globalEvent.data);
    }
  });

  /**
   * ToolTip Format
   */
  const toolTipFormatter = (params) => {
    const decrease = params.find((param) => param.seriesId === 'decrease');
    const increase = params.find((param) => param.seriesId === 'increase');
    // const targetData = params.find((param) => param.seriesId === 'target');
    // const currentData = params.find((param) => param.seriesId === 'current');
    const targetData = params.filter((p) => p.seriesId.startsWith('target'));
    const currentData = params.filter((p) => p.seriesId.startsWith('current'));
    const planData = params.find((param) => param.seriesId === 'plan');
    const title = `<b>${decrease.name}</b>`;
    const plan = planData ? `<div>${planData.marker} ${planData.seriesName}: ${kpiValue(planData.value, fieldKpi.unitValue)}</div>` : '';
    if (targetData.some((t) => t.data !== 0)) {
      // return `${title}<div>${targetData.marker} ${targetData.seriesName}: ${kpiValue(targetData.value, fieldKpi.unitValue)}</div>${plan}`;
      let total = 0;
      const tips = targetData
        .map((t) => {
          total += t.value;
          return `<div>${t.marker} ${t.seriesName}: ${kpiValue(t.value, fieldKpi.unitValue)}</div>`;
        })
        .reverse();
      tips.unshift(title + `<b>: ${kpiValue(total, fieldKpi.unitValue)}</b>`);
      tips.push(plan);
      return tips.join('\n');
    }
    if (currentData.some((c) => c.data !== 0)) {
      // return `${title}<div>${currentData.marker} ${currentData.seriesName}: ${kpiValue(
      //   currentData.value,
      //   fieldKpi.unitValue
      // )}</div>${plan}`;
      let total = 0;
      const tips = currentData
        .map((c) => {
          total += c.value;
          return `<div>${c.marker} ${c.seriesName}: ${kpiValue(c.value, fieldKpi.unitValue)}</div>`;
        })
        .reverse();
      tips.unshift(title + `<b>: ${kpiValue(total, fieldKpi.unitValue)}</b>`);
      tips.push(plan);
      return tips.join('\n');
    }
    const increaseValue = totalShow ? increase.data.increase : increase.data;
    const decreaseValue = totalShow ? decrease.data.decrease : decrease.data;
    return `
      ${title}
      <div>${increase.marker} ${increase.seriesName}: ${kpiValue(increaseValue, fieldKpi.unitValue)}</div>
      <div>${decrease.marker} ${decrease.seriesName}: ${kpiValue(decreaseValue, fieldKpi.unitValue)}</div>
      ${plan}
    `;
  };

  useEffect(() => {
    setChartLoading(true);
  }, [tralitSettings.account]);

  // Chart Option
  const options = useMemo(() => {
    const { fields, targetData: target, currentData: current } = dataProvider;

    const targetData =
      chartFilter.length > 0
        ? target.filter((item) => {
            return chartFilter.every((filter) => filter.value.includes(item[toCamelCase(filter.fieldKey)]));
          })
        : target;
    const currentData =
      chartFilter.length > 0
        ? current.filter((item) => {
            return chartFilter.every((filter) => filter.value.includes(item[toCamelCase(filter.fieldKey)]));
          })
        : current;

    // 最小粒度分组Keys
    const groupKeys = [
      'fiscalYear',
      ...fields.filter((field) => field.category === 'drill_down').map((field) => toCamelCase(field.fieldKey)),
    ];

    const groupAndSummarize = (top: number) => {
      // Step 1: 按 dist 分组
      const groupFunc = (item) => {
        const groupKeys = compareFieldKey.split('&');
        return groupKeys.map((key) => item[toCamelCase(key)]).join('&');
      };
      const curGrouped = groupBy(currentData, groupFunc);
      const tarGrouped = groupBy(targetData, groupFunc);
      // Step 2: 计算每个分组的 value 总和
      const isAllTarget = Object.keys(tarGrouped).length === 1 && Object.keys(tarGrouped)[0] === ALL_VALUE;
      const groupKeys = union(Object.keys(curGrouped), Object.keys(tarGrouped));
      if (isAllTarget) {
        return [
          {
            [compareFieldKey]: ALL_LABEL,
          },
        ];
      }
      const grouped = groupKeys.map((key) => {
        const curGroup = curGrouped[key];
        const tarGroup = tarGrouped[key];
        const curTotalValue = sumBy(curGroup, toCamelCase(fieldKpi.fieldKey));
        const tarTotalValue = sumBy(tarGroup, toCamelCase(fieldKpi.fieldKey));
        const showKey = key === ALL_VALUE ? ALL_LABEL : key;
        if (tarTotalValue >= curTotalValue) {
          return {
            [compareFieldKey]: showKey,
            increase: 0,
            decrease: tarTotalValue - curTotalValue,
          };
        } else {
          return {
            [compareFieldKey]: showKey,
            increase: curTotalValue - tarTotalValue,
            decrease: 0,
          };
        }
      });
      const summed = grouped.filter((item) => !(item.increase === 0 && item.decrease === 0));
      if (sortShow === SORT_SHOW.NAME) {
        // Step 3: 按 name 降序排序，并取前6个
        const sorted = orderBy(summed, [compareFieldKey], ['asc']);
        const topData = sorted.slice(0, top);
        const rest = sorted.slice(top);
        // Step 4: 合并剩下的为 "その他"
        if (rest.length > 0) {
          const result = [...topData, { [compareFieldKey]: OTHER_LABEL }];
          return result;
        }
        return topData;
      }
      if (sortShow === SORT_SHOW.TREND) {
        // Step 3: 按 decrease降序 increase升序排序，并取前6个 ['decrease', 'increase'], ['desc', 'asc']
        const sorted = orderBy(summed, ['decrease', 'increase'], ['desc', 'asc']);
        const topData = sorted.slice(0, top);
        const rest = sorted.slice(top);
        // Step 4: 合并剩下的为 "その他"
        if (rest.length > 0) {
          const result = [...topData, { [compareFieldKey]: OTHER_LABEL }];
          return result;
        }
        return topData;
      }
      if (sortShow === SORT_SHOW.DEVIATION) {
        // Step 3: 按 decrease降序 increase升序排序，并取前6个 ['decrease', 'increase'], ['desc', 'asc']
        const sorted = orderBy(
          summed,
          (item) => {
            const value = item.increase || item.decrease;
            return value;
          },
          ['desc']
        );
        const topData = sorted.slice(0, top);
        const rest = sorted.slice(top);
        // Step 4: 合并剩下的为 "その他"
        if (rest.length > 0) {
          const result = [...topData, { [compareFieldKey]: OTHER_LABEL }];
          return result;
        }
        return topData;
      }
      return [];
    };
    const limit = 6;
    const compareFieldInfos = groupAndSummarize(limit);
    const compareFieldAllValues = compareFieldInfos.map((compareFieldInfo) => compareFieldInfo[compareFieldKey]);
    const compareFieldValues = compareFieldAllValues.filter((compareFieldKey) => compareFieldKey !== OTHER_LABEL);
    const xAxisData = [tarLabel, ...compareFieldAllValues, curLabel];
    const beforeTotal = sumBy(targetData, toCamelCase(fieldKpi.fieldKey));
    const afterTotal = sumBy(currentData, toCamelCase(fieldKpi.fieldKey));
    // Merge All Data
    const beforeData = [beforeTotal, ...compareFieldAllValues.map(() => 0), 0];
    const afterData = [0, ...compareFieldAllValues.map(() => 0), afterTotal];
    // Separate Data
    const sortKeys = [...targetData, ...currentData]
      .reduce((pr, c) => {
        if (!pr.includes(c[toCamelCase(compareFieldKey)])) {
          pr.push(c[toCamelCase(compareFieldKey)]);
        }
        return pr;
      }, [])
      .sort((a, b) => (a < b ? -1 : 1))
      .slice(0, limit);
    const sortSeq = sortKeys.map((c, i) => [c, i]);
    const sortSeqObj = Object.fromEntries(sortSeq);
    const ks = sortSeq.map((s) => s[0]);
    const tar = targetData.reduce((pr, c) => {
      if (ks.includes(c[toCamelCase(compareFieldKey)])) {
        if (pr[c[toCamelCase(compareFieldKey)]]) {
          pr[c[toCamelCase(compareFieldKey)]] += c[toCamelCase(fieldKpi.fieldKey)];
        } else {
          pr[c[toCamelCase(compareFieldKey)]] = c[toCamelCase(fieldKpi.fieldKey)];
        }
      } else {
        if (pr[OTHER_LABEL]) {
          pr[OTHER_LABEL] += c[toCamelCase(fieldKpi.fieldKey)];
        } else {
          pr[OTHER_LABEL] = c[toCamelCase(fieldKpi.fieldKey)];
        }
      }
      return pr;
    }, {} as any);
    const newBeforeData = Object.entries<any>(tar).reduce((pr, [k, v]) => {
      pr[k] = [v, ...compareFieldAllValues.map(() => 0), 0];
      return pr;
    }, {} as any);
    const cur = currentData.reduce((pr, c) => {
      if (ks.includes(c[toCamelCase(compareFieldKey)])) {
        if (pr[c[toCamelCase(compareFieldKey)]]) {
          pr[c[toCamelCase(compareFieldKey)]] += c[toCamelCase(fieldKpi.fieldKey)];
        } else {
          pr[c[toCamelCase(compareFieldKey)]] = c[toCamelCase(fieldKpi.fieldKey)];
        }
      } else {
        if (pr[OTHER_LABEL]) {
          pr[OTHER_LABEL] += c[toCamelCase(fieldKpi.fieldKey)];
        } else {
          pr[OTHER_LABEL] = c[toCamelCase(fieldKpi.fieldKey)];
        }
      }
      return pr;
    }, {} as any);
    const newAfterData = Object.entries<any>(cur).reduce((pr, [k, v]) => {
      pr[k] = [0, ...compareFieldAllValues.map(() => 0), v];
      return pr;
    }, {} as any);
    const planDataAll = [
      ...xAxisData.map((item) =>
        sumBy(
          planData.filter((item) =>
            showCondition.every((f) => {
              return f.value.includes(item[toCamelCase(f.fieldKey)]);
            })
          ),
          toCamelCase(fieldKpi.fieldKey)
        )
      ),
    ];

    const compareData = compareFieldAllValues.reduce(
      (org, fieldValue) => {
        const beforeCompare = targetData.filter((item) => {
          if (fieldValue === ALL_LABEL) {
            return true;
          }
          if (fieldValue === OTHER_LABEL) {
            return !compareFieldValues.some((values) =>
              values.split('&').every((value, index) => item[toCamelCase(compareFieldKey.split('&')[index])] === value)
            );
          }
          return compareFieldKey.split('&').every((key, index) => {
            const value = item[toCamelCase(key)] === ALL_VALUE ? ALL_LABEL : item[toCamelCase(key)];
            return fieldValue.split('&')[index] === value;
          });
        });
        const afterCompare = currentData.filter((item) => {
          if (fieldValue === ALL_LABEL) {
            return true;
          }
          if (fieldValue === OTHER_LABEL) {
            return !compareFieldValues.some((values) =>
              values.split('&').every((value, index) => item[toCamelCase(compareFieldKey.split('&')[index])] === value)
            );
          }
          return compareFieldKey.split('&').every((key, index) => {
            const value = item[toCamelCase(key)] === ALL_VALUE ? ALL_LABEL : item[toCamelCase(key)];
            return fieldValue.split('&')[index] === value;
          });
        });

        const beforeGroup = groupBy(beforeCompare, (item) => groupKeys.map((key) => item[key]).join('&'));
        const afterGroup = groupBy(afterCompare, (item) => groupKeys.map((key) => item[key]).join('&'));
        const data = uniq([...Object.keys(beforeGroup), ...Object.keys(afterGroup)]).reduce(
          (acc, cur) => {
            const beforeData = beforeGroup[cur];
            const afterData = afterGroup[cur];
            if (beforeData && afterData) {
              if (afterData[0][toCamelCase(fieldKpi.fieldKey)] > beforeData[0][toCamelCase(fieldKpi.fieldKey)]) {
                acc.increase += afterData[0][toCamelCase(fieldKpi.fieldKey)] - beforeData[0][toCamelCase(fieldKpi.fieldKey)];
              }
              if (afterData[0][toCamelCase(fieldKpi.fieldKey)] < beforeData[0][toCamelCase(fieldKpi.fieldKey)]) {
                acc.decrease += beforeData[0][toCamelCase(fieldKpi.fieldKey)] - afterData[0][toCamelCase(fieldKpi.fieldKey)];
              }
            }
            if (afterData && !beforeData) {
              const value = afterData[0][toCamelCase(fieldKpi.fieldKey)];
              if (value > 0) {
                acc.increase += value;
              }
              if (value < 0) {
                acc.decrease += -value;
              }
            }
            if (beforeData && !afterData) {
              const value = beforeData[0][toCamelCase(fieldKpi.fieldKey)];
              if (value > 0) {
                acc.decrease += value;
              }
              if (value < 0) {
                acc.increase += -value;
              }
            }
            return acc;
          },
          { increase: 0, decrease: 0 }
        );
        if (totalShow) {
          if (data.increase >= data.decrease) {
            const increase = data.increase - data.decrease;
            org.increase.push({
              value: increase,
              increase: data.increase,
              decrease: data.decrease,
            });
            org.decrease.push({
              value: 0,
              increase: data.increase,
              decrease: data.decrease,
            });
            org.placeholder.push(org.lastValue);
            org.lastValue = org.lastValue + increase;
          } else {
            const decrease = data.decrease - data.increase;
            org.decrease.push({
              value: decrease,
              increase: data.increase,
              decrease: data.decrease,
            });
            org.increase.push({
              value: 0,
              increase: data.increase,
              decrease: data.decrease,
            });
            org.placeholder.push(org.lastValue - decrease);
            org.lastValue = org.lastValue - decrease;
          }
        } else {
          const placeholder = org.lastValue - data.decrease;
          org.decrease.push(data.decrease);
          org.placeholder.push(placeholder);
          org.increase.push(data.increase);
          org.lastValue = placeholder + data.increase;
        }
        return org;
      },
      {
        lastValue: beforeTotal,
        placeholder: [],
        increase: [],
        decrease: [],
      }
    );

    let tarDataSet = null;
    let curDataSet = null;
    if (mergeAll) {
      tarDataSet = [
        {
          id: 'target',
          name: tarLabel,
          type: 'bar',
          xAxisIndex: 1,
          barGap: '-100%',
          data: beforeData,
          itemStyle: {
            color: color.primaryColor,
          },
          label: {
            position: 'top',
            fontSize: fontSize.chart,
            formatter: (params) => (params.dataIndex === 0 ? kpiValue(params.value, fieldKpi.unitValue) : ''),
          },
          barMaxWidth: 100,
        },
      ];
      curDataSet = [
        {
          id: 'current',
          name: curLabel,
          type: 'bar',
          xAxisIndex: 1,
          barGap: '-100%',
          data: afterData,
          itemStyle: {
            color: color.primaryColor,
          },
          label: {
            position: 'top',
            fontSize: fontSize.chart,
            formatter: (params) => (params.dataIndex === xAxisData.length - 1 ? kpiValue(params.value, fieldKpi.unitValue) : ''),
          },
          barMaxWidth: 100,
        },
      ];
    } else {
      tarDataSet = Object.entries<any>(newBeforeData)
        .sort((a, b) => (sortSeqObj[a[0]] < sortSeqObj[b[0]] ? -1 : 1))
        .map(([k, v], i, arr) => {
          let lastPosIndex = -1;
          arr.forEach(([ak, av], i) => {
            if (av[0] >= 0) {
              lastPosIndex = i;
            }
          });
          return {
            id: `target${i}`,
            name: k,
            stack: 'before',
            type: 'bar',
            xAxisIndex: 1,
            barGap: '-100%',
            data: v,
            label: {
              position: 'top',
              fontSize: fontSize.chart,
              formatter: (params) => {
                return i === lastPosIndex && params.dataIndex === 0 ? kpiValue(beforeTotal, fieldKpi.unitValue) : '';
              },
            },
            barMaxWidth: 100,
          };
        });
      curDataSet = Object.entries<any>(newAfterData)
        .sort((a, b) => (sortSeqObj[a[0]] < sortSeqObj[b[0]] ? -1 : 1))
        .map(([k, v], i, arr) => {
          let lastPosIndex = -1;
          arr.forEach(([ak, av], i) => {
            if (av[0] >= 0) {
              lastPosIndex = i;
            }
          });
          return {
            id: `current${i}`,
            name: k,
            stack: 'after',
            type: 'bar',
            xAxisIndex: 1,
            barGap: '-100%',
            data: v,
            label: {
              position: 'top',
              fontSize: fontSize.chart,
              formatter: (params) =>
                i === lastPosIndex && params.dataIndex === xAxisData.length - 1 ? kpiValue(afterTotal, fieldKpi.unitValue) : '',
            },
            barMaxWidth: 100,
          };
        });
    }

    return {
      title: {
        value: title,
        styles: {
          fontSize: fontSize.chartTitle,
          margin: 0,
        },
      },
      tooltips: ['zoomIn', 'reset'],
      chartOptions: {
        animation: false,
        dataZoom: [
          {
            type: 'slider',
            yAxisIndex: 0,
            filterMode: 'none',
            showDetail: false,
          },
          {
            type: 'inside',
            yAxisIndex: 0,
            filterMode: 'none',
          },
        ],
        xAxis: [
          {
            type: 'category',
            nameGap: 10,
            axisLabel: {
              interval: 0,
              hideOverlap: false,
              fontSize: fontSize.chart,
              // formatter: (value) => dayjs(value).format(dimension.format),
            },
            nameTextStyle: {
              fontSize: fontSize.chart,
            },

            data: xAxisData,
          },
          {
            show: false,
            type: 'category',
            nameGap: 10,
            axisLabel: {
              fontSize: fontSize.chart,
              // formatter: (value) => dayjs(value).format(dimension.format),
            },
            nameTextStyle: {
              fontSize: fontSize.chart,
            },
            data: xAxisData,
          },
        ],
        yAxis: [
          {
            type: 'value',
            name: `値${fieldKpi.unitName ? `(${fieldKpi.unitName})` : ''}`,
            nameGap: 10,
            axisLabel: {
              fontSize: fontSize.chart,
              formatter: (value) => kpiValue(value, fieldKpi.unitValue),
            },
            nameTextStyle: {
              fontSize: fontSize.chart,
            },
            scale: true,
            // min: minY,
          },
        ],
        grid: {
          top: 45,
          right: 60,
          bottom: 40,
          left: 85,
        },
        legend: {
          show: false,
        },
        tooltip: {
          trigger: 'axis',
          appendToBody: true,
          axisPointer: {
            lineStyle: {
              width: 2,
            },
          },
          formatter: toolTipFormatter,
        },
        series: [
          ...tarDataSet,
          ...(totalShow
            ? [
                {
                  id: 'placeholder',
                  name: 'placeholder',
                  type: 'bar',
                  stack: 'diff',
                  itemStyle: {
                    borderColor: 'transparent',
                    color: 'transparent',
                  },
                  emphasis: {
                    itemStyle: {
                      borderColor: 'transparent',
                      color: 'transparent',
                    },
                  },
                  xAxisIndex: 0,
                  data: [0, ...compareData.placeholder, 0],
                  label: {
                    show: false,
                    fontSize: fontSize.chart,
                    color: 'transparent',
                  },
                  barMaxWidth: 50,
                },
                {
                  id: 'decrease',
                  name: '未達分',
                  type: 'bar',
                  stack: 'diff',
                  itemStyle: {
                    color: color.errorColor,
                  },
                  xAxisIndex: 0,
                  data: [0, ...compareData.decrease, 0],
                  label: {
                    position: 'top',
                    fontSize: fontSize.chart,
                    formatter: (params) =>
                      params.dataIndex !== 0 && params.dataIndex !== xAxisData.length - 1 && params.value
                        ? `-${kpiValue(params.value, fieldKpi.unitValue)}`
                        : '',
                    color: color.errorColor,
                  },
                  barMaxWidth: 50,
                },
                {
                  id: 'increase',
                  name: '超過分',
                  type: 'bar',
                  stack: 'diff',
                  itemStyle: {
                    color: color.successColor,
                  },
                  xAxisIndex: 0,
                  data: [0, ...compareData.increase, 0],
                  label: {
                    position: 'top',
                    fontSize: fontSize.chart,
                    formatter: (params) =>
                      params.dataIndex !== 0 && params.dataIndex !== xAxisData.length - 1 && params.value
                        ? kpiValue(params.value, fieldKpi.unitValue)
                        : '',
                    color: color.successColor,
                  },
                  barMaxWidth: 50,
                },
              ]
            : [
                {
                  id: 'decreasePlaceholder',
                  name: 'decreasePlaceholder',
                  type: 'bar',
                  stack: 'decrease',
                  itemStyle: {
                    borderColor: 'transparent',
                    color: 'transparent',
                  },
                  emphasis: {
                    itemStyle: {
                      borderColor: 'transparent',
                      color: 'transparent',
                    },
                  },
                  xAxisIndex: 0,
                  data: [0, ...compareData.placeholder, 0],
                  label: {
                    show: false,
                    color: 'transparent',
                  },
                  barMaxWidth: 50,
                },
                {
                  id: 'increasePlaceholder',
                  name: 'increasePlaceholder',
                  type: 'bar',
                  stack: 'increase',
                  itemStyle: {
                    borderColor: 'transparent',
                    color: 'transparent',
                  },
                  emphasis: {
                    itemStyle: {
                      borderColor: 'transparent',
                      color: 'transparent',
                    },
                  },
                  xAxisIndex: 0,
                  data: [0, ...compareData.placeholder, 0],
                  label: {
                    show: false,
                    color: 'transparent',
                  },
                  barMaxWidth: 50,
                },
                {
                  id: 'decrease',
                  name: '未達分',
                  type: 'bar',
                  stack: 'decrease',
                  itemStyle: {
                    color: color.errorColor,
                  },
                  xAxisIndex: 0,
                  data: [0, ...compareData.decrease, 0],
                  label: {
                    position: 'top',
                    fontSize: fontSize.chart,
                    formatter: (params) =>
                      params.dataIndex !== 0 && params.dataIndex !== xAxisData.length - 1
                        ? `-${kpiValue(params.value, fieldKpi.unitValue)}`
                        : '',
                    color: color.errorColor,
                  },
                  barMaxWidth: 50,
                },
                {
                  id: 'increase',
                  name: '超過分',
                  type: 'bar',
                  stack: 'increase',
                  itemStyle: {
                    color: color.successColor,
                  },
                  xAxisIndex: 0,
                  data: [0, ...compareData.increase, 0],
                  label: {
                    position: 'top',
                    fontSize: fontSize.chart,
                    formatter: (params) =>
                      params.dataIndex !== 0 && params.dataIndex !== xAxisData.length - 1 ? kpiValue(params.value, fieldKpi.unitValue) : '',
                    color: color.successColor,
                  },
                  barMaxWidth: 50,
                },
              ]),
          ...curDataSet,
          ...(showPlan && planDataAll.every((value) => value)
            ? [
                {
                  id: 'plan',
                  name: label.plan,
                  type: 'line',
                  symbol: 'none',
                  data: planDataAll,
                  silent: true,
                  itemStyle: {
                    color: color.warningColor,
                    borderType: 'dotted',
                  },
                  lineStyle: {
                    type: 'dashed',
                  },
                  label: {
                    show: false,
                  },
                },
              ]
            : []),
        ],
      },
      height: 'calc(100% - 80px)',
    };
  }, [
    title,
    dataProvider,
    chartFilter,
    compareFieldKey,
    fieldKpi,
    tarLabel,
    curLabel,
    totalShow,
    sortShow,
    fontSize,
    planData,
    showCondition,
  ]);

  const seriesClickHandler = (e) => {
    if ((e.seriesName === '未達分' || e.seriesName === '超過分') && !e.name.includes(OTHER_LABEL)) {
      if (action === 'drillDown') {
        if (e.name !== ALL_LABEL) {
          const eventInfo = {
            eventType: GLOBAL_EVENT.CHART_ITEM_CLICK,
            data: { fieldKey: compareFieldKey, value: [e.name], seriesName: e.seriesName, compare },
            pageId: PAGE_ID.COMPARE,
          };
          event$.emit(eventInfo);
        }
      } else {
        if (compare === COMPARE.SNAPSHOT_RATIO) {
          const params = {
            actionCompare: compare,
          };
          const paramsStr = new URLSearchParams(Object.entries(params)).toString();
          navigate(`${property.action}?${paramsStr}`);
        }
        if (compare === COMPARE.PLANNED_RATIO) {
          if (e.name === ALL_LABEL) {
            const index = drillFields.findIndex((field) => field.fieldKey === compareFieldKey);
            const next = drillFields[index + 1];
            const params = {
              compareDrillKeys: next.fieldKey,
              actionCompare: compare,
            };
            const paramsStr = new URLSearchParams(Object.entries(params)).toString();
            navigate(`${property.action}?${paramsStr}`);
          } else {
            const index = drillFields.findIndex((field) => field.fieldKey === compareFieldKey);
            const next = drillFields[index + 1];
            const params = {
              compareDrillKeys: next.fieldKey,
              [camelToSnake(compareFieldKey)]: e.name,
              actionCompare: compare,
            };
            const paramsStr = new URLSearchParams(Object.entries(params)).toString();
            navigate(`${property.action}?${paramsStr}`);
          }
        }
      }
    }
  };

  const bodyHighlight = useMemo(() => {
    const params = Object.fromEntries(searchParams);
    const actionCompare = params.actionCompare;
    if (property.i === 'p0202c01' && actionCompare === COMPARE.PLANNED_RATIO) {
      return 'highlight-card';
    }
    if (property.i === 'p0202c02' && actionCompare === COMPARE.SNAPSHOT_RATIO) {
      return 'highlight-card';
    }
    return '';
  }, [property, searchParams]);

  return (
    <>
      <Card
        style={{ height: '100%' }}
        styles={{ body: { height: '100%', padding: 0 } }}
        // classNames={{
        //   body: bodyHighlight,
        // }}
      >
        <ContentChart
          contentList={[]}
          {...options}
          extra={{
            value: extra && (
              <Space size="large" style={{ marginRight: 12 }}>
                <Space>
                  <Flex align="center">差分表示方法: </Flex>
                  <Flex align="center">
                    <Switch
                      checkedChildren="合計"
                      unCheckedChildren="増減"
                      checked={totalShow}
                      style={{ background: color.primaryColor }}
                      onChange={(checked) => {
                        setTotalShow(checked);
                      }}
                    />
                  </Flex>
                </Space>
                <Space>
                  <Flex align="center">内訳表示順: </Flex>
                  <Flex align="center">
                    <Select
                      size="small"
                      style={{ width: 80 }}
                      value={sortShow}
                      onChange={(value) => {
                        setSortShow(value);
                        event$.emit({
                          eventType: GLOBAL_EVENT.SORT_CHANGE,
                          value,
                          pageId: PAGE_ID.COMPARE,
                        });
                      }}
                      options={[
                        {
                          key: SORT_SHOW.NAME,
                          label: '名称順',
                          value: SORT_SHOW.NAME,
                        },
                        {
                          key: SORT_SHOW.TREND,
                          label: '増減順',
                          value: SORT_SHOW.TREND,
                        },
                        {
                          key: SORT_SHOW.DEVIATION,
                          label: '乖離順',
                          value: SORT_SHOW.DEVIATION,
                        },
                      ]}
                    />
                  </Flex>
                </Space>
                <Space>
                  <Flex align="center">内訳項目: </Flex>
                  <Flex align="center">
                    {compareFieldKey.includes('&') ? (
                      <span>
                        {compareFieldKey
                          .split('&')
                          .map((fieldKey) => drillFields.find((field) => field.fieldKey === fieldKey).label)
                          .join(', ')}
                      </span>
                    ) : (
                      <Select
                        size="small"
                        style={{ width: 150 }}
                        value={drillFields.length > 0 ? compareFieldKey : null}
                        options={drillFields.map((field) => ({
                          key: field.fieldKey,
                          label: field.label,
                          value: field.fieldKey,
                        }))}
                        onChange={(value) => {
                          const eventInfo = {
                            eventType: GLOBAL_EVENT.CHART_ITEM_CLICK,
                            data: { fieldKey: value },
                            pageId: PAGE_ID.COMPARE,
                          };
                          event$.emit(eventInfo);
                        }}
                      />
                    )}
                  </Flex>
                </Space>
              </Space>
            ),
          }}
          showSeriesLabel
          mouseWheelZoomLock={false}
          loading={chartLoading}
          height={'90%'}
          tooltips={['zoomIn']}
          isBank={!dataProvider || !dataProvider.currentData || dataProvider.currentData.length === 0}
          onClick={seriesClickHandler}
        />
      </Card>
    </>
  );
};

export default CustomizeCompareChart;
